<script setup lang="ts">
import { computed, onBeforeMount, onMounted, provide, ref } from 'vue';
import { getTemplateMessages } from './services/supabase.service';
import store from './store';

const loading = ref<boolean>(false);
provide('isLoading', loading);

onBeforeMount(async () => {
  loading.value = true;
  const { data } = await getTemplateMessages();
  store.commit('SAVE_MESSAGES', data ?? {});
  loading.value = false;
});

onMounted(() =>
  store.commit('UPDATE_CART', JSON.parse(localStorage.getItem('cart') ?? '[]')),
);

const isMobile = computed(() => /Mobi|Android/i.test(navigator.userAgent));
provide('isMobile', isMobile.value);

const username = ref<string>('Kamu');
provide('userName', username.value);
const showUsernamePrompt = ref<boolean>(false);

onBeforeMount(() => {
  const name = localStorage.getItem('username');
  if (name) {
    username.value = name;
  } else {
    showUsernamePrompt.value = true;
  }
});

const submitName = (e: any) => {
  username.value = e.target?.elements?.username.value;
  showUsernamePrompt.value = false;
  localStorage.setItem('username', username.value);
};

const greetingMessage = computed(() => `Hai, ${username.value}!`);
provide('greetingMessage', greetingMessage);
</script>
<template>
  <router-view />
  <Dialog
    :visible="showUsernamePrompt"
    modal
    style="width: 80vw; max-width: 450px"
  >
    <template #container>
      <div class="title">
        {{ store.state.template_messages.welcome_title }}
      </div>
      <p v-html="store.state.template_messages.welcome_message"></p>
      <form @submit.prevent="submitName" class="input-user-name">
        <label>
          <input
            type="text"
            placeholder="Masukin nama kamu..."
            name="username"
            required
          />
        </label>
        <button type="submit" class="button primary">Okay, Lanjut</button>
      </form>
    </template>
  </Dialog>
</template>
<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&display=swap');
@import '~scss/index';

* {
  font-family: 'Montserrat', system-ui;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  transition: 0.15s all ease;
}

html {
  scroll-behavior: smooth;
}

html,
body {
  overflow: hidden;
  height: 100%;
  font-size: clamp(12px, 3.6vw, 16px);
}

img {
  font-size: 0;
}

.iconify {
  cursor: pointer;
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 26px;

  .message {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    color: $heading-color;
    font-style: normal;
    line-height: normal;

    .greeting {
      font-size: 0.925rem;
      font-weight: 600;
    }

    .prompt {
      font-size: 0.8675rem;
      font-weight: 400;
    }
  }
}

h1.page-title {
  color: $heading-color;
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-transform: capitalize;
}

::-webkit-scrollbar {
  height: 2px;
  width: 0;
}

#app {
  width: 100vw;
  height: 100%;
  position: relative;
  overflow-y: auto;
}

a {
  all: unset;
  cursor: pointer;
}

.top-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9;
  position: absolute;
  padding: 24px !important;
  top: 0;
  left: 0;
  right: 0;

  .back {
    width: 22px;
    height: 22px;
    border-radius: 50%;
  }
  .back:hover,
  .back:active {
    background: $bg-weak;
  }
}

button.p-galleria-close.p-link {
  top: 24px;
  right: 24px;
  width: max-content;
  height: max-content;
  padding: 6px;

  svg {
    width: 1.15rem;
    height: 1.15rem;
  }
}

.p-galleria-thumbnail-item-content img {
  width: 60px;
  height: 60px;
}

.p-galleria-thumbnail-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 12px;
}

.p-galleria-thumbnail-items-container {
  display: flex;
  justify-content: center;
  gap: 12px;

  .p-galleria-thumbnail-items {
    display: flex;
    gap: 12px;
  }
}

button.button {
  all: unset;
  border-radius: 8px;
  text-align: center;
  font-size: 0.925rem;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  padding: 10px 18px !important;
  cursor: pointer;
}

button.button.secondary {
  background: $bg-weak;
  color: $dark-icon-color;
}

button.button.text {
  color: $dark-icon-color;
}

button.button.text:active {
  background: $bg-weak;
}

button.button.primary {
  background: $primary;
  color: white;
}

button.button.primary-outlined {
  background: white;
  color: $primary;
  border: 1px solid;
}

button.out_of_stock {
  display: flex;
  padding: 10px 18px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  border-radius: 8px;
  background: $bg-weak;
  border: none;
}

button.button.secondary.danger {
  background: $bg-weak;
  color: $badge-color;
}

button.button.secondary.danger:active {
  background: $badge-color;
  color: $bg-weak;
}

button.button.icon-right,
button.button.icon-left {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 auto;
}

.p-dialog .p-dialog-footer {
  display: flex;
  gap: 8px;

  .p-button-text {
    all: unset;
  }
}

.product-container.list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-container.grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;

  .product {
    display: flex;
    flex-direction: column;
    padding: 0;
    gap: 0;

    .p-skeleton.product__image {
      height: 30vw !important;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    .product__image img {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    .detail {
      padding: 10px 16px 16px;
    }
  }
}

.top-navigation.searh-page {
  position: sticky;
  gap: 16px;

  .searchbox {
    flex-grow: 1;
    flex-direction: row-reverse;
    border-radius: 10px;
    padding-left: 1rem;
    border: 1px solid $bg-weak;
    background: white;
  }
}

.search-message {
  font-size: 0.925rem;
  padding: 22px 0 0;
  font-weight: 400;
  line-height: 22px;

  span {
    font-weight: 600;
    text-transform: capitalize;
  }
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 22px 0 6px;
}

.sticky {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 9;
  background: white;
  padding: 24px 0 16px;
}

.p-dropdown-panel.p-component {
  transition: none;
}

.product {
  display: grid;
  grid-template-columns: 77px auto;
  width: 100%;
  padding: 16px;
  gap: 26px;

  border-radius: 20px;
  background: $product-bg;
  font-style: normal;
  line-height: normal;

  .product__image {
    height: max-content;
    border-radius: 10px;

    img {
      border-radius: 10px;
      width: 100%;
      height: 100%;
      aspect-ratio: 1/1;
      object-fit: cover;
    }
  }

  .detail {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 0.95rem;
    flex: 1 0 0;

    .product__name {
      color: $heading-color;
      font-size: 1rem;
      font-weight: 600;
    }

    .product__tagline {
      color: $heading-color;
      font-size: 0.85rem;
      font-weight: 300;
    }
  }
}

.product__price {
  display: flex;
  gap: 6px;

  .price-normal,
  .price-discount {
    color: $price-color;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: normal;
  }

  .price-normal.discounted {
    text-decoration: line-through;
    color: $heading-color;
    font-size: 0.875rem;
  }
}

.p-carousel {
  cursor: pointer;
  position: relative;

  .p-carousel-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .p-carousel-items-container {
    transition: transform 0.3s ease;
  }

  .p-carousel-item {
    border-radius: 20px;
    width: 100%;

    .p-image {
      width: 100%;

      .p-image-preview-indicator {
        border: none;
        color: white;
      }

      img.mobile {
        width: 100%;
        aspect-ratio: 16/9;
        border-radius: 20px;
        object-fit: cover;
      }

      img.desktop {
        width: 100%;
        aspect-ratio: 880/240;
        border-radius: 20px;
        object-fit: cover;
      }
    }
  }

  .p-carousel-prev,
  .p-carousel-next {
    position: absolute;
    background-color: #fff;
    color: $dark-icon-color;
    border: none;
    border-radius: 50%;
    padding: 5px;
    cursor: pointer;
    box-shadow: 0px 3px 19px rgba(0, 0, 0, 0.1);

    .p-carousel-prev-disabled,
    .p-carousel-next-disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .p-carousel-prev {
    left: 0;
    transform: translateX(-50%);
    z-index: 2;
  }

  .p-carousel-next {
    right: 0;
    transform: translateX(50%);
  }

  .p-carousel-indicators {
    list-style: none;
    display: flex;
    justify-content: center;
    gap: 3px;
    padding: 0;

    .p-carousel-indicator {
      button {
        background-color: $innactive-bullet-color;
        border: none;
        border-radius: 50%;
        width: 6px;
        height: 6px;
        cursor: pointer;

        &::-moz-focus-inner {
          border: 0;
        }

        &:focus {
          outline: none;
        }
      }

      &[data-p-highlight='true'] {
        button {
          border-radius: 13px;
          background-color: $active-bullet-color;
          width: 14px;
          height: 6px;
        }
      }
    }
  }
}
</style>
